import { type MouseEvent, type ReactNode, useId, useRef } from "react";
import { cn } from "../../../lib/utils";
import { Text } from "../../../primitives";

export type PanelFieldControlProps = {
	/** Put on the control so the visible label names it and a click on the label reaches it. */
	id: string;
	/** Id of the visible label. Put it on `aria-labelledby` of a group control (radio group, segmented control). */
	labelId: string;
};

export type PanelFieldProps = {
	label: string;
	required?: boolean;
	/** Helper text under the field. */
	helper?: ReactNode;
	className?: string;
	testId?: string;
	/** Names the field's wrapper a group after the label, for children that take no `id`. */
	group?: boolean;
	/** A function receives the ids that tie the visible label to the control. */
	children: ReactNode | ((control: PanelFieldControlProps) => ReactNode);
};

const NATIVELY_LABELABLE = "input, select, textarea, button, meter, output, progress";
const FOCUSABLE =
	'[tabindex="0"], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [href]';

/**
 * Label (red asterisk when required), field, and helper text for the sheets. The label always
 * points at the control: native inputs take the click themselves, and for anything else (a radio
 * group, a segmented control, plain children without the `id`) a click moves focus to the
 * control's first focusable element.
 */
export const PanelField = ({
	label,
	required,
	helper,
	className,
	testId,
	group,
	children,
}: PanelFieldProps) => {
	const controlId = useId();
	const labelId = useId();
	const fieldRef = useRef<HTMLDivElement>(null);

	const focusControl = (event: MouseEvent<HTMLLabelElement>) => {
		const control = document.getElementById(controlId);
		if (control?.matches(NATIVELY_LABELABLE)) return;
		const scope = control ?? fieldRef.current;
		const target = scope?.querySelector<HTMLElement>(FOCUSABLE) ?? control;
		if (!target) return;
		event.preventDefault();
		target.focus();
	};

	return (
		<div
			ref={fieldRef}
			className={cn("flex min-w-0 flex-col gap-1.5", className)}
			role={group ? "group" : undefined}
			aria-labelledby={group ? labelId : undefined}
			data-testid={testId}
		>
			<label id={labelId} htmlFor={controlId} onClick={focusControl} className="self-start">
				<Text type="caption" color="secondary">
					{label}
					{required && (
						<Text type="caption" className="ml-0.5 text-intent-danger-text" aria-hidden>
							*
						</Text>
					)}
				</Text>
			</label>
			{typeof children === "function" ? children({ id: controlId, labelId }) : children}
			{helper && (
				<Text type="caption" color="tertiary" className="font-normal">
					{helper}
				</Text>
			)}
		</div>
	);
};
